import React, { useState, useRef, useCallback, useEffect } from "react";

interface ImageCarouselProps {
  images: string[];
  alt: string;
  autoPlay?: boolean;
  interval?: number;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, alt, autoPlay = true, interval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (index < 0) setCurrentIndex(images.length - 1);
      else if (index >= images.length) setCurrentIndex(0);
      else setCurrentIndex(index);
    },
    [images.length]
  );

  // Auto-play
  useEffect(() => {
    if (!autoPlay || images.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoPlay, interval, images.length]);

  // Reset timer on manual interaction
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoPlay && images.length > 1) {
      timerRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, interval);
    }
  }, [autoPlay, interval, images.length]);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    goTo(currentIndex - 1);
    resetTimer();
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    goTo(currentIndex + 1);
    resetTimer();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    if (Math.abs(touchDeltaX.current) > 50) {
      if (touchDeltaX.current > 0) {
        goTo(currentIndex - 1);
      } else {
        goTo(currentIndex + 1);
      }
      resetTimer();
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  if (images.length === 0) return null;

  if (images.length === 1) {
    return (
      <div className="project-carousel">
        <div className="project-carousel-slide">
          <img src={images[0]} alt={alt} loading="lazy" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="project-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="project-carousel-track"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((img, i) => (
          <div key={i} className="project-carousel-slide">
            <img src={img} alt={`${alt} ${i + 1}`} loading="lazy" />
          </div>
        ))}
      </div>

      <button className="project-carousel-arrow left" onClick={handlePrev}>
        <i className="fa-solid fa-chevron-left" />
      </button>
      <button className="project-carousel-arrow right" onClick={handleNext}>
        <i className="fa-solid fa-chevron-right" />
      </button>

      <div className="project-carousel-dots">
        {images.map((_, i) => (
          <button
            key={i}
            className={`project-carousel-dot${i === currentIndex ? " active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex(i);
              resetTimer();
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
